// Vacío por defecto: las peticiones van a rutas relativas al mismo origen
// desde el que se sirve el frontend, y es Nginx (ver nginx.conf) quien las
// reenvía internamente al contenedor del backend. Así funciona igual si
// accedes por IP local, por dominio, o por lo que sea — sin tener que fijar
// ninguna dirección en el momento de compilar.
const API_URL = import.meta.env.VITE_API_URL || "";

function getToken() {
  return localStorage.getItem("centinel_token");
}

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Las respuestas de exportación (texto plano) se devuelven tal cual
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    if (!res.ok) throw new Error(`Error ${res.status}`);
    return res;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Si la llamada iba autenticada y el servidor dice 401, la sesión ya no
    // vale (caducada o token inválido) — se limpia y se vuelve al login en
    // vez de dejar al usuario viendo el error sin poder hacer nada.
    if (auth && res.status === 401) {
      clearSession();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    const err = new Error(data.error || `Error ${res.status}`);
    err.transcript = data.transcript;
    throw err;
  }
  return data;
}

export const api = {
  API_URL,
  login: (callsign, password) =>
    request("/api/auth/login", { method: "POST", body: { callsign, password }, auth: false }),
  changePassword: (currentPassword, newPassword) =>
    request("/api/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),
  checkSetupToken: (token) => request(`/api/auth/setup-password/${token}`, { auth: false }),
  setupPassword: (token, newPassword) =>
    request("/api/auth/setup-password", { method: "POST", body: { token, newPassword }, auth: false }),

  // Usuarios (superadmin)
  listUsers: () => request("/api/users"),
  getDebugLog: (callsign) => request(`/api/users/debug-log${callsign ? `?callsign=${encodeURIComponent(callsign)}` : ""}`),
  searchUsers: (q) => request(`/api/users/search?q=${encodeURIComponent(q)}`),
  createUser: (payload) => request("/api/users", { method: "POST", body: payload }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: "DELETE" }),
  updateUser: (id, payload) => request(`/api/users/${id}`, { method: "PATCH", body: payload }),
  resetPassword: (userId, password) =>
    request(`/api/users/${userId}/reset-password`, { method: "POST", body: { password } }),

  // Clubs
  listClubs: () => request("/api/clubs"),
  createClub: (payload) => request("/api/clubs", { method: "POST", body: payload }),
  listClubMembers: (clubId) => request(`/api/clubs/${clubId}/members`),
  addClubMember: (clubId, userId, role) =>
    request(`/api/clubs/${clubId}/members`, { method: "POST", body: { userId, role } }),
  removeClubMember: (clubId, userId) =>
    request(`/api/clubs/${clubId}/members/${userId}`, { method: "DELETE" }),

  // Actividades
  listActivities: () => request("/api/activities"),
  getActivity: (id) => request(`/api/activities/${id}`),
  createActivity: (payload) => request("/api/activities", { method: "POST", body: payload }),
  updateActivity: (id, payload) => request(`/api/activities/${id}`, { method: "PATCH", body: payload }),
  sendReportNow: (id) => request(`/api/activities/${id}/send-report`, { method: "POST" }),
  deleteActivity: (id) => request(`/api/activities/${id}`, { method: "DELETE" }),
  deleteClub: (id) => request(`/api/clubs/${id}`, { method: "DELETE" }),
  listOperators: (activityId) => request(`/api/activities/${activityId}/operators`),
  getStats: (activityId) => request(`/api/activities/${activityId}/stats`),
  getPropagation: (activityId) => request(`/api/activities/${activityId}/propagation`),
  addOperator: (activityId, userId, role) =>
    request(`/api/activities/${activityId}/operators`, { method: "POST", body: { userId, role } }),
  removeOperator: (activityId, userId) =>
    request(`/api/activities/${activityId}/operators/${userId}`, { method: "DELETE" }),

  // QSOs
  createQso: (activityId, payload) =>
    request(`/api/activities/${activityId}/qsos`, { method: "POST", body: payload }),
  listQsos: (activityId, scope = "mine") =>
    request(`/api/activities/${activityId}/qsos?scope=${scope}`),
  deleteQso: (activityId, qsoId) =>
    request(`/api/activities/${activityId}/qsos/${qsoId}`, { method: "DELETE" }),
  updateQso: (activityId, qsoId, payload) =>
    request(`/api/activities/${activityId}/qsos/${qsoId}`, { method: "PUT", body: payload }),
  getQsoEdits: (activityId, qsoId) =>
    request(`/api/activities/${activityId}/qsos/${qsoId}/edits`),
  nextExchange: (activityId, band, mode) =>
    request(`/api/activities/${activityId}/qsos/next-exchange?band=${encodeURIComponent(band)}&mode=${encodeURIComponent(mode)}`),
  listExcludedQsos: (activityId) => request(`/api/activities/${activityId}/qsos/excluded`),
  getExchangeGaps: (activityId) => request(`/api/activities/${activityId}/qsos/exchange-gaps`),
  // Descarga con autenticación real (un <a href> normal no puede mandar el
  // token, así que se hace con fetch + blob y se dispara la descarga a mano)
  downloadExport: async (activityId, format, scope, suggestedName) => {
    const token = localStorage.getItem("centinel_token");
    const res = await fetch(
      `${API_URL}/api/activities/${activityId}/qsos/export?format=${format}&scope=${scope}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Error ${res.status} al exportar`);
    }
    const blob = await res.blob();
    const ext = format === "cabrillo" ? "log" : "adi";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${suggestedName || "log"}_${scope}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Importar log ADIF — mode: "preview" | "confirm". operatorId opcional
  // (solo lo usa el organizador para subir en nombre de otro operador).
  importAdif: async (activityId, file, mode, operatorId) => {
    const token = localStorage.getItem("centinel_token");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
    if (operatorId) formData.append("operatorId", operatorId);
    const res = await fetch(`${API_URL}/api/activities/${activityId}/qsos/import`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status} al importar`);
    return data;
  },

  // Público (sin auth)
  publicActivities: () => request("/api/public/activities", { auth: false }),
  publicMatrix: (activityId) => request(`/api/public/activities/${activityId}/matrix`, { auth: false }),
  publicRecentQsos: (activityId) => request(`/api/public/activities/${activityId}/recent-qsos`, { auth: false }),
  publicCountries: (activityId) => request(`/api/public/activities/${activityId}/countries`, { auth: false }),

  // Perfil propio (credenciales de DX Cluster, QRZ, HamQTH)
  getProfile: () => request("/api/profile"),
  updateProfile: (payload) => request("/api/profile", { method: "PUT", body: payload }),
  updateDxClusterCreds: (payload) => request("/api/profile/dxcluster", { method: "PUT", body: payload }),
  configureDxClusterProfile: () => request("/api/profile/dxcluster/configure", { method: "POST" }),
  updateQrzCreds: (payload) => request("/api/profile/qrz", { method: "PUT", body: payload }),
  updateHamqthCreds: (payload) => request("/api/profile/hamqth", { method: "PUT", body: payload }),

  // Logo de actividad (multipart, no pasa por el request() genérico con JSON)
  uploadActivityLogo: async (activityId, file) => {
    const token = localStorage.getItem("centinel_token");
    const formData = new FormData();
    formData.append("logo", file);
    const res = await fetch(`${API_URL}/api/activities/${activityId}/logo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    return data;
  },

  // Plantilla de QSL (multipart)
  uploadQslTemplate: async (activityId, file) => {
    const token = localStorage.getItem("centinel_token");
    const formData = new FormData();
    formData.append("template", file);
    const res = await fetch(`${API_URL}/api/activities/${activityId}/qsl-template`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    return data;
  },

  // QSL pública (sin login)
  publicQslInfo: (activityId) => request(`/api/public/activities/${activityId}/qsl-info`, { auth: false }),
  publicQslSearch: (activityId, callsign) =>
    request(`/api/public/activities/${activityId}/qsl?callsign=${encodeURIComponent(callsign)}`, { auth: false }),
  qslContactImageUrl: (activityId, qsoId) => `${API_URL}/api/public/activities/${activityId}/qsl/${qsoId}/image`,
  qslSummaryImageUrl: (activityId, callsign) => `${API_URL}/api/public/activities/${activityId}/qsl/summary/image?callsign=${encodeURIComponent(callsign)}`,

  // Consulta de indicativo (QRZ con respaldo en HamQTH)
  lookupCallsign: (activityId, callsign) =>
    request(`/api/activities/${activityId}/lookup?callsign=${encodeURIComponent(callsign)}`),
};

export function saveSession(token, user) {
  localStorage.setItem("centinel_token", token);
  localStorage.setItem("centinel_user", JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem("centinel_token");
  localStorage.removeItem("centinel_user");
}
export function getSession() {
  const token = getToken();
  const raw = localStorage.getItem("centinel_user");
  if (!token || !raw) return null;
  try {
    return { token, user: JSON.parse(raw) };
  } catch {
    return null;
  }
}
